import { env } from '@/env';
import { currentGrantId } from '@/state/impersonation';
import i18next from 'i18next';

/** Idioma ativo da consola — o servidor responde (erros, Hexis) neste idioma. */
const localeHeaders = (): Record<string, string> => (i18next.language ? { 'Accept-Language': i18next.language } : {});

// Cliente único. A consola fala com a MESMA API (/api/v1) que a app lean; o
// token de sessão Clerk é injetado por getToken (ligado no arranque).
const BASE = `${env.apiOrigin}/api/v1`;

type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter) {
  tokenGetter = fn;
}

/** A API respondeu, mas com um status de erro. `status` é sempre real (>= 400). */
export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * O pedido NUNCA chegou a ter resposta: CORS, DNS, offline, TLS.
 *
 * Distinto do ApiError de propósito. Confundir os dois foi um bug real
 * (2026-07-16): o `CORS_ORIGIN` da API não tinha o domínio da consola, o
 * /access/me era bloqueado pelo browser, e o StaffGate — que fazia
 * `if (isError || !isStaff)` — acusava a CONTA de "não pertencer à equipa".
 * Uma mensagem falsa que mandava investigar permissões durante um problema de
 * infraestrutura.
 */
export class NetworkError extends Error {
  constructor(public cause: unknown) {
    super('Não foi possível contactar a API');
    this.name = 'NetworkError';
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export async function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

/** Upload multipart — passa FormData; o Content-Type (com boundary) é do browser. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  return request<T>(path, { method: 'POST', body: form });
}

/**
 * POST que devolve o `Response` CRU — para SSE, onde o corpo é um fio e não um
 * JSON. Não passa pelo `request()` de propósito: aquele lê o corpo inteiro
 * (`res.json()`), que é exatamente o que não se pode fazer a um stream.
 *
 * Leva os mesmos cabeçalhos das outras chamadas — incluindo a concessão de
 * IMPERSONAÇÃO: um stream aberto enquanto se opera como outra pessoa tem de ir
 * com a mesma identidade, senão o Hexis responderia sobre a conta errada.
 */
export async function apiStream(path: string, body: unknown): Promise<Response> {
  const token = await tokenGetter();
  return fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...localeHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(currentGrantId() ? { 'x-impersonate-grant': currentGrantId()! } : {}),
    },
    body: JSON.stringify(body),
  });
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const token = await tokenGetter();
  // Multipart (FormData): NÃO impor application/json — o browser define o
  // Content-Type com o boundary correto. Impor JSON parte o upload.
  const isMultipart = typeof FormData !== 'undefined' && init.body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...localeHeaders(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Concessão de impersonação (se ativa): referência revalidada pelo
        // servidor a CADA pedido — expirada/revogada é ignorada em silêncio.
        ...(currentGrantId() ? { 'x-impersonate-grant': currentGrantId()! } : {}),
        ...init.headers,
      },
    });
  } catch (cause) {
    // O fetch só rejeita quando NÃO houve resposta (CORS, DNS, offline, TLS).
    // Sem este catch o erro chega às páginas como um TypeError anónimo
    // ("Failed to fetch") e fica indistinguível de um 403.
    throw new NetworkError(cause);
  }
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text().catch(() => undefined); }
    throw new ApiError(res.status, `API ${res.status} em ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
